/** Dados de execução “fixados” no editor (localStorage), estilo pin do n8n. */

const STORAGE_PREFIX = 'chatwoot:workflow-editor-pinned-exec:v1:';

export function pinnedExecutionStorageKey(accountId, workflowId) {
  return `${STORAGE_PREFIX}${accountId}:${workflowId}`;
}

export function readPinnedExecution(accountId, workflowId) {
  if (accountId == null || workflowId == null) return null;
  try {
    const raw = localStorage.getItem(
      pinnedExecutionStorageKey(accountId, workflowId)
    );
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return null;
    return data;
  } catch {
    return null;
  }
}

export function writePinnedExecution(accountId, workflowId, payload) {
  if (accountId == null || workflowId == null) return;
  localStorage.setItem(
    pinnedExecutionStorageKey(accountId, workflowId),
    JSON.stringify(payload)
  );
}

export function clearPinnedExecution(accountId, workflowId) {
  if (accountId == null || workflowId == null) return;
  localStorage.removeItem(pinnedExecutionStorageKey(accountId, workflowId));
}

/** Último snapshot de bindings nos passos da execução (contexto visto pelo último nó de ação). */
export function lastBindingsFromExecutionTrace(trace) {
  if (!Array.isArray(trace)) return {};
  for (let i = trace.length - 1; i >= 0; i -= 1) {
    const b = trace[i]?.input?.bindings;
    if (
      b &&
      typeof b === 'object' &&
      !Array.isArray(b) &&
      Object.keys(b).length > 0
    ) {
      try {
        return JSON.parse(JSON.stringify(b));
      } catch {
        return { ...b };
      }
    }
  }
  return {};
}

function cloneBindingsDeep(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  try {
    return JSON.parse(JSON.stringify(raw));
  } catch {
    return { ...raw };
  }
}

function ensureUpstreamBranch(bindings) {
  if (!bindings.upstream || typeof bindings.upstream !== 'object') {
    bindings.upstream = {};
  }
  const up = bindings.upstream;
  if (!up.http_response || typeof up.http_response !== 'object') {
    up.http_response = { body: '', status_code: '', headers_json: '{}' };
  }
  if (!up.ai_response || typeof up.ai_response !== 'object') {
    up.ai_response = {
      message: '',
      prompt_tokens: '',
      completion_tokens: '',
      total_tokens: '',
    };
  }
}

/** Alinha à forma de `WorkflowVariableBindings#normalize_upstream_http` (strings). */
function upstreamHttpFromTraceOutput(out) {
  if (!out || typeof out !== 'object') return null;
  return {
    body: String(out.body ?? ''),
    status_code:
      out.status_code != null && out.status_code !== ''
        ? String(out.status_code)
        : '',
    headers_json: String(out.headers_json ?? '{}'),
  };
}

/** Alinha à forma de `WorkflowVariableBindings#normalize_upstream_ai` (strings). */
function upstreamAiFromTraceOutput(out) {
  if (!out || typeof out !== 'object') return null;
  const usage = out.usage && typeof out.usage === 'object' ? out.usage : {};
  const n = v =>
    v !== undefined && v !== null && v !== '' ? String(v) : '';
  return {
    message: String(out.message ?? ''),
    prompt_tokens: n(usage.prompt_tokens ?? usage.promptTokens),
    completion_tokens: n(usage.completion_tokens ?? usage.completionTokens),
    total_tokens: n(usage.total_tokens ?? usage.totalTokens),
  };
}

/**
 * Bindings para fixar no editor: último `input.bindings` do trace +
 * `upstream` enriquecido com as saídas dos passos `http_request` / `ai_agent`.
 * O último snapshot sozinho ainda não inclui a resposta do próprio passo `ai_agent`
 * em curso (o servidor grava `output` depois).
 */
export function buildPinnedBindingsFromTrace(trace) {
  const bindings = cloneBindingsDeep(lastBindingsFromExecutionTrace(trace));
  ensureUpstreamBranch(bindings);
  if (!Array.isArray(trace)) return bindings;

  trace.forEach(step => {
    if (!step || step.node_type !== 'action') return;
    if (String(step.status || '') !== 'success') return;
    const kind = String(step.kind || '');
    const out = step.output;
    if (!out || typeof out !== 'object') return;
    if (kind === 'http_request') {
      const h = upstreamHttpFromTraceOutput(out);
      if (h) Object.assign(bindings.upstream.http_response, h);
    }
    if (kind === 'ai_agent') {
      const a = upstreamAiFromTraceOutput(out);
      if (a) Object.assign(bindings.upstream.ai_response, a);
    }
  });
  return bindings;
}

/** Resolve `{{a.b.c}}` contra o objeto de bindings. */
export function resolveWorkflowToken(bindings, token) {
  if (!bindings || token == null || token === '') return null;
  const m = String(token).match(/^\{\{([^}]+)\}\}$/);
  if (!m) return null;
  const path = m[1]
    .trim()
    .split('.')
    .map(s => s.trim())
    .filter(Boolean);
  let cur = bindings;
  for (const p of path) {
    if (cur == null || typeof cur !== 'object') return null;
    cur = cur[p];
  }
  if (cur === undefined) return null;
  return cur;
}

export function formatPinnedSampleValue(value, maxLen = 280) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string') {
    const s = value;
    return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s;
  }
  try {
    const s = JSON.stringify(value);
    return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s;
  } catch {
    return String(value);
  }
}
